import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Video, Upload, X, Loader2, ImageIcon } from 'lucide-react';
import { lotesService } from '../../services/lotesService';
import { comprimirVideo, TAMANHO_MINIMO_COMPRIMIR } from '../../utils/comprimirVideo';

const VIDEO_MAX_BYTES = 250 * 1024 * 1024; // 250MB — limite do arquivo enviado (depois da compressão)
const VIDEO_MAX_ORIGINAL_BYTES = 1024 * 1024 * 1024; // 1GB — aceito só se a compressão no navegador funcionar
const VIDEO_TIPOS_ACEITOS = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo'];
const FOTO_MAX_BYTES = 15 * 1024 * 1024; // 15MB
const FOTO_TIPOS_ACEITOS = ['image/jpeg', 'image/png', 'image/webp'];

// Gera uma capa (thumbnail) a partir do próprio arquivo de vídeo, direto no navegador —
// mesma técnica do VideoUploadLote.jsx (não reutilizada por import pra não acoplar
// os dois componentes; ver comentário no editor de animais).
function gerarPosterBlob(file) {
  return new Promise((resolve) => {
    let resolvido = false;
    const finalizar = (blob) => {
      if (resolvido) return;
      resolvido = true;
      resolve(blob || null);
    };

    try {
      const videoEl = document.createElement('video');
      videoEl.preload = 'metadata';
      videoEl.muted = true;
      videoEl.playsInline = true;
      const objectUrl = URL.createObjectURL(file);
      videoEl.src = objectUrl;

      const limpar = () => URL.revokeObjectURL(objectUrl);

      videoEl.addEventListener('loadedmetadata', () => {
        videoEl.currentTime = Math.min(1, (videoEl.duration || 1) / 2);
      });
      videoEl.addEventListener('seeked', () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = videoEl.videoWidth || 320;
          canvas.height = videoEl.videoHeight || 180;
          canvas.getContext('2d').drawImage(videoEl, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => { limpar(); finalizar(blob); }, 'image/jpeg', 0.75);
        } catch {
          limpar();
          finalizar(null);
        }
      });
      videoEl.addEventListener('error', () => { limpar(); finalizar(null); });
      setTimeout(() => { limpar(); finalizar(null); }, 8000);
    } catch {
      finalizar(null);
    }
  });
}

async function enviarArquivoPendente(organizadorId, leilaoId, kind, blob, filename, contentType, onProgress) {
  const { upload_url, fields, key } = await lotesService.presignVideoLotePendente(
    organizadorId, leilaoId, { kind, filename, content_type: contentType, file_size: blob.size }
  );

  const form = new FormData();
  Object.entries(fields).forEach(([k, v]) => form.append(k, v));
  form.append('file', blob);

  await axios.post(upload_url, form, {
    onUploadProgress: onProgress ? (evt) => {
      if (evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100));
    } : undefined,
  });

  const { url } = await lotesService.confirmarVideoLotePendente(organizadorId, leilaoId, key, kind);
  return { url, pendingKey: key };
}

/**
 * Upload de vídeo (com poster automático) e foto para um ANIMAL individual do
 * lote (fluxo cavalo). Sempre usa o modo "pendente" leilão-scoped — os IDs dos
 * animais mudam a cada save (substituição em bloco), então não há modo
 * "definitivo" por animal como existe para o lote em VideoUploadLote.jsx.
 * A promoção pra pasta definitiva acontece no backend, dentro da rota
 * PUT .../lotes/{lote_id}/animais.
 */
export function AnimalVideoUpload({ organizadorId, leilaoId, videoUrl, posterUrl, onChange, disabled }) {
  const inputRef = useRef(null);
  const [enviando, setEnviando] = useState(false);
  const [etapa, setEtapa] = useState('');
  const [progresso, setProgresso] = useState(0);
  const [erro, setErro] = useState('');

  const handleArquivo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setErro('');
    if (!VIDEO_TIPOS_ACEITOS.includes(file.type)) {
      setErro('Formato não suportado. Use MP4, MOV, WEBM ou AVI.');
      return;
    }
    if (file.size > VIDEO_MAX_ORIGINAL_BYTES) {
      setErro('Vídeo excede o limite de 1GB.');
      return;
    }

    setEnviando(true);
    setProgresso(0);
    try {
      // Comprime no navegador (720p, índice no início); se não der, envia o original.
      let arquivoEnvio = file;
      let nomeEnvio = file.name;
      let tipoEnvio = file.type;
      if (file.size >= TAMANHO_MINIMO_COMPRIMIR) {
        setEtapa('Otimizando vídeo...');
        const comprimido = await comprimirVideo(file, setProgresso);
        if (comprimido) {
          arquivoEnvio = comprimido;
          nomeEnvio = file.name.replace(/\.[^.]+$/, '') + '.mp4';
          tipoEnvio = 'video/mp4';
        }
        setProgresso(0);
      }
      if (arquivoEnvio.size > VIDEO_MAX_BYTES) {
        setErro('Vídeo excede o limite de 250MB.');
        return;
      }

      setEtapa('Enviando vídeo...');
      const video = await enviarArquivoPendente(organizadorId, leilaoId, 'video', arquivoEnvio, nomeEnvio, tipoEnvio, setProgresso);
      onChange?.({ video_url: video.url, video_pending_key: video.pendingKey });

      setEtapa('Gerando capa...');
      setProgresso(0);
      const posterBlob = await gerarPosterBlob(file);
      if (posterBlob) {
        const poster = await enviarArquivoPendente(organizadorId, leilaoId, 'poster', posterBlob, 'poster.jpg', 'image/jpeg');
        onChange?.({ video_poster_url: poster.url, poster_pending_key: poster.pendingKey });
      }
    } catch (err) {
      console.error('Erro ao enviar vídeo do animal:', err);
      setErro(err.response?.data?.detail || 'Falha ao enviar o vídeo. Tente novamente.');
    } finally {
      setEnviando(false);
      setEtapa('');
      setProgresso(0);
    }
  };

  const handleRemover = () => {
    onChange?.({ video_url: null, video_poster_url: null, video_pending_key: null, poster_pending_key: null });
  };

  return (
    <div>
      <label className="flex items-center gap-2 text-xs font-medium text-gray-500 mb-1">
        <Video size={14} className="text-red-500" />
        Vídeo do Animal (upload, até 1GB — otimizado automaticamente)
      </label>

      <input
        ref={inputRef}
        type="file"
        accept="video/mp4,video/quicktime,video/webm,video/x-msvideo"
        className="hidden"
        onChange={handleArquivo}
        disabled={disabled || enviando}
      />

      {videoUrl ? (
        <div className="flex items-center gap-3">
          <video src={videoUrl} poster={posterUrl || undefined} controls preload="none" className="w-32 h-20 rounded-lg bg-black object-contain" />
          {!disabled && (
            <button type="button" onClick={handleRemover} className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700">
              <X size={14} /> Remover
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || enviando}
          className={`w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed rounded-lg text-xs ${
            disabled || enviando ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600'
          }`}
        >
          {enviando ? (
            <>
              <Loader2 size={14} className="animate-spin" /> {etapa} {etapa === 'Enviando vídeo...' ? `${progresso}%` : ''}
            </>
          ) : (
            <>
              <Upload size={14} /> Selecionar vídeo
            </>
          )}
        </button>
      )}

      {erro && <p className="text-xs text-red-600 mt-1">{erro}</p>}
    </div>
  );
}

export function AnimalFotoUpload({ organizadorId, leilaoId, fotoUrl, onChange, disabled }) {
  const inputRef = useRef(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  const handleArquivo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setErro('');
    if (!FOTO_TIPOS_ACEITOS.includes(file.type)) {
      setErro('Formato não suportado. Use JPG, PNG ou WEBP.');
      return;
    }
    if (file.size > FOTO_MAX_BYTES) {
      setErro('Foto excede o limite de 15MB.');
      return;
    }

    setEnviando(true);
    try {
      const foto = await enviarArquivoPendente(organizadorId, leilaoId, 'foto', file, file.name, file.type);
      onChange?.({ foto_url: foto.url, foto_pending_key: foto.pendingKey });
    } catch (err) {
      console.error('Erro ao enviar foto do animal:', err);
      setErro(err.response?.data?.detail || 'Falha ao enviar a foto. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  const handleRemover = () => {
    onChange?.({ foto_url: null, foto_pending_key: null });
  };

  return (
    <div>
      <label className="flex items-center gap-2 text-xs font-medium text-gray-500 mb-1">
        <ImageIcon size={14} className="text-blue-500" />
        Foto do Animal
      </label>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={handleArquivo}
        disabled={disabled || enviando}
      />

      {fotoUrl ? (
        <div className="flex items-center gap-3">
          <img src={fotoUrl} alt="Foto do animal" className="w-20 h-20 rounded-lg object-cover border border-gray-200" />
          {!disabled && (
            <button type="button" onClick={handleRemover} className="flex items-center gap-1 text-xs text-red-600 hover:text-red-700">
              <X size={14} /> Remover
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || enviando}
          className={`w-20 h-20 flex flex-col items-center justify-center gap-1 border-2 border-dashed rounded-lg text-xs ${
            disabled || enviando ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-600 hover:border-blue-400 hover:text-blue-600'
          }`}
        >
          {enviando ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
        </button>
      )}

      {erro && <p className="text-xs text-red-600 mt-1">{erro}</p>}
    </div>
  );
}
